import React, { useContext, useEffect, useState } from 'react';
import Header from '../../../components/Common/Header/Header';
import ContentApp from '../../../components/ContentApp';
import { WelletContext } from '../../../context/WelletContext';
import { getQueryString } from '../../../helpers/url';
import { useDispatch, useSelector } from 'react-redux';
import {
  addExtraProduct,
  removeExtraProduct,
  setExtraProductList,
} from '../../../actions/extraProductActions';
import { useTranslation } from 'react-i18next';

import ButtonReservation from '../../../components/Common/Button/ButtonReservation';
import Money from '../../../components/Common/Money/Money';
import { setReservation } from '../../../actions/reservationActions';
import ChevronIcon from '../../../components/icons/chevronIcon';
import styles from '../ReservationCheck/reservationCheck.module.css';
import { CircleSpinner } from 'react-spinners-kit';
import EventHeader from '../../../components/Common/EventHeader/EventHeader';
import { format } from 'date-fns';
import ProductModal from '../../../components/Common/ProductModal/ProductModal';
import ReservationTicketInfo from '../ReservationTicketInfo/ReservationTicketInfo';
import { fetchProducts } from '../../../services/product/productService';
import EventProductCard from '../../../components/EventProductCard/EventProductCard';
import SkeletonReservationArea from '../../../components/Skeletons/SkeletonReservationArea';
import ProductCard from '../../../components/ProductCard/ProductCard';
import ErrorCard from '../../../components/ErrorCard/ErrorCard';
import SeatMap from '../../../components/SeatMap/SeatMap';
import { getDatesWithEvents, fetchEventDetails } from '../../../services/EventsServices';
import {
  formatDateTime,
  formatDatSemiShort,
  getDateFromOffset,
  getLocale,
} from '../../../helpers/dates';
import DatePickerSlider from '../../../components/DatePickerSlider/DatePickerSlider';
import TimePickerSlider from '../../../components/TimePickerSlider/TimePickerSlider';
import EmptyActivity from '../../../components/Common/EmptyActivity/EmptyActivity';
import CurrencySelectorWithPicker from '../../../components/CurrencySelector/CurrencySelectorWithPicker';
import { sendMessageToParent } from '../../../helpers/iframe';
import usePurchaseRules from '../../../hooks/usePurchaseRules';

const CalendarTicketSelector = ({ history }) => {
  const { t } = useTranslation();
  const app = useSelector((state) => state.app);
  const currency = useSelector((state) => state.currency?.selected?.shortForm);
  const locale = getLocale(app.language);

  const dispatch = useDispatch();
  const welletContext = useContext(WelletContext);

  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [seatProducts, setSeatProducts] = useState([]);
  const [groupProducts, setGroupProducts] = useState([]);
  const { ruleError, ruleErrorRef, validateRules, clearRuleErrorIfSatisfied, clearRuleError } =
    usePurchaseRules(products);
  const extraProducts = useSelector((state) => state.extraProducts.items);

  const [loading, setLoading] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingImage, setLoadingImage] = useState(true);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const idReservation = getQueryString('id');
  const showId = getQueryString('showId');

  const [selectedProductCategory, setSelectedProductCategory] = useState(null);
  const [datesWithEvents, setDatesWithEvents] = useState([]);
  const [show, setShow] = useState(null);
  const [today, setToday] = useState(null);
  const [todayDate, setTodayDate] = useState(null);
  const [dates, setDates] = useState([]);
  const [datesCalendar, setDatesCalendar] = useState([]);
  const [listTimes, setListTimes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [emptyEvents, setEmptyEvents] = useState(false);

  const orderType = ['ADULTS', 'ADOLESCENTS', 'CHILDREN', 'INFANTS', 'SENIORS', 'ALL'];

  const queryParams = new URLSearchParams({
    showId: showId,
  });

  if (idReservation !== null && idReservation !== undefined) {
    queryParams.append('id', idReservation);
  }

  let query = `?${queryParams.toString()}`;

  const getShow = async () => {
    try {
      const result = await welletContext.apis.tickets.get(`/shows/get/`, {
        params: {
          showId: showId,
          lang: app.language,
          details: false,
          reservationType: 'RESTAURANT',
          images: true,
          videos: false,
          IsFeature: true,
        },
      });

      setShow(result.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getEvents = async (date) => {
    setLoadingEvents(true);
    try {
      const first10Days = await getDatesWithEvents(showId, date, app.language, 16, welletContext);

      const mappedDates = first10Days.map((event) => ({
        date: formatDateTime(event.date),
        hasEvents: event.eventTimes.length > 0,
      }));

      setDates(mappedDates);

      if (first10Days.length === 0) {
        setEmptyEvents(true);
        setLoadingEvents(false);
        return;
      }

      setEmptyEvents(false);
      setDatesWithEvents(first10Days);
      setSelectedDate(first10Days[0].date);

      const itemSelected = first10Days[0];

      const firstEvent = itemSelected.eventTimes[0].events[0];
      setSelectedEvent(firstEvent);
      getEventDetails(firstEvent.eventId, itemSelected.date);

      setSelectedTime(itemSelected.eventTimes[0].time);
      setListTimes(itemSelected.eventTimes.map((t) => t.time));
    } catch (error) {
      console.error(error);
      setError(t('callApiError'));
    } finally {
      setLoadingEvents(false);
    }
  };

  const getCalendarEvents = async (date) => {
    try {
      const newEvents = await getDatesWithEvents(showId, date, app.language, 365, welletContext);
      setDatesCalendar(
        newEvents.map((event) => ({
          date: formatDateTime(event.date),
          hasEvents: event.eventTimes.length > 0,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getEventDetails = async (id, date) => {
    setLoadingProducts(true);

    try {
      const response = await fetchEventDetails(welletContext, id, app.language);
      setEventDetail(response);
      setSelectedProductCategory(response.productCategories[0]);

      if (response.productCategories.length > 0) {
        await getProducts(date, response.id, response.productCategories[0].id);
      } else {
        setError(t('productsNotAvailables'));
        setProducts([]);
        setGroupProducts([]);
        setLoadingProducts(false);
      }
    } catch (error) {
      console.error(error);
      setLoadingProducts(false);
    }
  };

  const getProducts = async (date, eventId, productCategoryId) => {
    setLoadingProducts(true);
    setError(null);

    try {
      const result = await fetchProducts(
        welletContext,
        showId,
        date,
        app.language,
        eventId,
        productCategoryId,
      );

      // if (result.data.products.length > 0) {
      //   setEmptyEvents(false);
      // }

      if (result && result.data) {
        let initializedProducts = result.data.products.map((product) => ({
          ...product,
          quantity: 0,
          disabled: false,
        }));

        const groupByType = initializedProducts.reduce((acc, item) => {
          const key = item.ageRange ? item.ageRange.name : 'ALL';
          if (!acc[key]) {
            acc[key] = {
              ageRangeId: item.ageRange?.name,
              ageRangeNumericId: item.ageRange?.id,
              ageRangeLabel: item.ageRange?.nameString,
              items: [],
            };
          }
          acc[key].items.push(item);
          return acc;
        }, {});

        setProducts(initializedProducts);
        setGroupProducts(groupByType);

        dispatch(setExtraProductList(result.data.extraProducts));

        if (initializedProducts.length === 0) setError(t('productsNotAvailables'));
      }
    } catch (error) {
      setError(
        error.response && error.response.data ? t('reservationNotAvailables') : t('callApiError'),
      );
      console.error(error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSelectDate = (date) => {
    clearRuleError();
    setSelectedDate(date);
    setSelectedProductCategory(null);

    const dayEvents = datesWithEvents.find((e) => e.date === date);
    setSelectedTime(dayEvents?.eventTimes[0].time);

    if (!dayEvents) {
      getEvents(date);
      return;
    }

    const event = dayEvents.eventTimes[0].events[0];
    getEventDetails(event.eventId, date);
    setSelectedEvent(event);
    setListTimes(dayEvents.eventTimes.map((t) => t.time));
  };

  const handleSelectTime = (time) => {
    clearRuleError();
    setSelectedTime(time);

    const dayEvents = datesWithEvents.find((e) => e.date === selectedDate);
    const event = dayEvents.eventTimes.find((e) => e.time === time).events[0];

    setSelectedEvent(event);
    getEventDetails(event.eventId, selectedDate);
  };

  const handleIncrement = (id) => {
    const index = products.findIndex((p) => p.id === id);
    const product = products[index];

    if (product.stock && product.stock === product.quantity) return;

    let newProducts = [...products];

    if (product.isExclusive) {
      newProducts
        .filter((p) => p.id !== product.id)
        .forEach((p) => {
          p.disabled = true;
          p.quantity = 0;
          dispatch(removeExtraProduct({ ...p, quantity: 0, specialType: 'EXTRAPAX' }));
        });
    }

    newProducts[index].disabled = false;

    if (newProducts[index].quantity === 0 && newProducts[index].minQuantity > 0) {
      newProducts[index].quantity = newProducts[index].minQuantity;
    } else {
      newProducts[index].quantity = newProducts[index].quantity + 1;
    }

    if (newProducts[index].minQuantity)
      newProducts[index].quantity = Math.max(
        newProducts[index].quantity,
        newProducts[index].minQuantity,
      );
    if (newProducts[index].stock)
      newProducts[index].quantity = Math.min(newProducts[index].quantity, newProducts[index].stock);

    setProducts(newProducts);

    clearRuleErrorIfSatisfied(newProducts);

    if (product.extraPax > 0) {
      dispatch(addExtraProduct({ ...product, quantity: 0, specialType: 'EXTRAPAX' }));
    }

    if (product.behavior === 'NO_GUARANTEED') {
      const reservationInfo = setReservationInfo();
      dispatch(setReservation(reservationInfo));
      history.push({
        pathname: '/reservation-no-guaranteed',
        search: query + '&eventId=' + selectedEvent.eventId,
      });
    }
  };

  // const handleIncrement = (id) => {
  //   const index = products.findIndex((p) => p.id === id);
  //   const product = products[index];

  //   if (product.stock && product.stock === product.quantity) return;

  //   let newProducts = [...products];

  //   if (product.isExclusive) {
  //     newProducts
  //       .filter((p) => p.id !== product.id)
  //       .forEach((p) => {
  //         p.disabled = true;
  //         p.quantity = 0;
  //         dispatch(removeExtraProduct({ ...p, quantity: 0, specialType: 'EXTRAPAX' }));
  //       });
  //   }

  //   newProducts[index].disabled = false;
  //   newProducts[index].quantity = newProducts[index].stock
  //     ? Math.min(newProducts[index].stock, newProducts[index].quantity + 1)
  //     : newProducts[index].quantity + 1;

  //   setProducts(newProducts);

  //   if (product.extraPax > 0) {
  //     dispatch(addExtraProduct({ ...product, quantity: 0, specialType: 'EXTRAPAX' }));
  //   }

  //   if (product.behavior === 'NO_GUARANTEED') {
  //     const reservationInfo = setReservationInfo();
  //     dispatch(setReservation(reservationInfo));
  //     history.push({
  //       pathname: '/reservation-no-guaranteed',
  //       search: query + '&eventId=' + selectedEvent.eventId,
  //     });
  //   }

  //   // uncomment the following block to disable multi product selection
  //   // if (product.reservationType === 'AREA') {
  //   //   const reservationInfo = setReservationInfo();
  //   //   dispatch(setReservation(reservationInfo));

  //   //   if (product.behavior === 'NO_GUARANTEED')
  //   //     history.push({
  //   //       pathname: '/reservation-no-guaranteed',
  //   //       search: query,
  //   //     });
  //   //   else if (product.extraPax > 0 || extraProducts.length > 0) {
  //   //     linkToExtraProducts();
  //   //   } else {
  //   //     handleContinue();
  //   //   }
  //   // }
  // };

  const handleIncrementSeat = (product) => {
    product.quantity = 1;
    setSeatProducts([...seatProducts, product]);
  };

  const handleDecrement = (id) => {
    const index = products.findIndex((p) => p.id === id);
    let newProducts = [...products];

    let newQty = newProducts[index].quantity - 1;

    if (
      newProducts[index].minQuantity != null &&
      newProducts[index].minQuantity > 0
    ) {
      if (newQty >= newProducts[index].minQuantity)
        newProducts[index].quantity = newQty;
      else newProducts[index].quantity = 0;
    } else {
      newProducts[index].quantity = Math.max(newQty, 0);
    }

    if (newProducts[index].isExclusive && newProducts[index].quantity === 0) {
      newProducts.forEach((p) => (p.disabled = false));
    }

    dispatch(
      removeExtraProduct({
        ...newProducts[index],
        quantity: 0,
        specialType: "EXTRAPAX",
      })
    );
    setProducts(newProducts);

    clearRuleErrorIfSatisfied(newProducts);
  };

  // const handleDecrement = (id) => {
  //   const index = products.findIndex((p) => p.id === id);
  //   let newProducts = [...products];

  //   newProducts[index].quantity = Math.max(0, newProducts[index].quantity - 1);

  //   if (newProducts[index].isExclusive && newProducts[index].quantity === 0) {
  //     newProducts.forEach((p) => (p.disabled = false));
  //   }
  //   dispatch(removeExtraProduct({ ...newProducts[index], quantity: 0, specialType: 'EXTRAPAX' }));
  //   setProducts(newProducts);
  // };

  const handleDecrementSeat = (index) => {
    setSeatProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCategoryClick = (category) => {
    clearRuleError();
    setSelectedProductCategory(category);
    getProducts(selectedEvent.startDate, selectedEvent.eventId, category.id);
  };

  const handleCalendarClick = () => {
    if (datesCalendar.length > 0) return;

    getCalendarEvents(format(todayDate, 'yyyy-MM-dd'));
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const onInfoClick = (id) => {
    setSelectedProduct(id);
    toggleModal();
  };

  const handleContinue = () => {
    if (!validateRules()) return;

    query += `&eventId=${selectedEvent.eventId}`;
    const reservationInfo = setReservationInfo();
    dispatch(setReservation(reservationInfo));

    if (extraProducts.length > 0) {
      linkToExtraProducts();
    } else if (eventDetail.hasTransportation) {
      history.push({
        pathname: '/pickup',
        search: query,
      });
    } else {
      history.push({
        pathname: '/reservation-check-tickets',
        search: query,
      });
    }
  };

  const linkToExtraProducts = () => {
    history.push({
      pathname: '/area-extra-reservation',
      search: query,
    });
  };

  const setReservationInfo = () => {
    let finalProducts = [];

    products
      .filter((product) => product.quantity > 0)
      .map((product) => {
        finalProducts.push({
          id: product.id,
          quantity: product.quantity,
          extraPaxs: 0,
          specialType: product.specialType,
          paxs: product.pax * product.quantity,
          ageRange: product.ageRange,
        });
      });

    seatProducts.forEach((product) => {
      finalProducts.push({
        id: product.id,
        quantity: product.quantity,
        extraPaxs: 0,
        specialType: product.specialType,
        seatProps: product.seatProps,
      });
    });

    const reservationInfo = {
      showId: showId,
      showName: eventDetail.name,
      products: finalProducts,
      date: selectedDate,
      hasTransportation: eventDetail.hasTransportation,
      performance: format(new Date(selectedEvent.startDate), 'HH:mm'),
      paxs: finalProducts.reduce((sum, item) => sum + item.paxs, 0),
      customerCurrency: currency,
    };

    return reservationInfo;
  };

  const sumTotal = () => {
    let total = 0;

    total += products
      .filter((p) => p.quantity > 0)
      .reduce((total, p) => total + p.prices.price * p.quantity, 0);

    total += seatProducts.reduce((total, p) => total + p.prices.price, 0);

    return total;
  };

  const totalConsume = () => {
    return products
      .filter((p) => p.quantity > 0)
      .reduce((total, p) => total + p.prices.consumption * p.quantity, 0);
  };

  const totalPax = () => {
    let paxs = 0;

    paxs += products
      .filter((p) => p.quantity > 0)
      .reduce((total, p) => total + p.pax * p.quantity, 0);

    paxs += seatProducts.reduce((total, p) => total + p.pax, 0);

    return paxs;
  };

  const renderTimes = () => {
    if (selectedEvent === null) return;

    return (
      <>
        {listTimes.length > 1 && (
          <div className='mt-2'>
            <TimePickerSlider
              times={listTimes}
              selectedTime={selectedTime}
              handleSelect={(e) => handleSelectTime(e)}
            />
          </div>
        )}
        <div className='font-small fw-600 mt-3'>
          <div className='row'>
            <div className='col'>{formatDatSemiShort(selectedEvent?.startDate, locale)}</div>
            <div className='col text-right'>
              {t('fromTimeToTime', {
                time: format(new Date(selectedEvent?.startDate), 'HH:mm aaa'),
                time2: format(new Date(selectedEvent?.endDate), 'HH:mm aaa'),
              })}
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderProductCategories = () => {
    if (!eventDetail || eventDetail.productCategories.length <= 1) return;

    return (
      <div className='row text-center font-normal2 mt-4 px-3'>
        {eventDetail.productCategories.map((category) => (
          <div
            key={category.id}
            onClick={() => handleCategoryClick(category)}
            className={`col p-2 ${
              selectedProductCategory?.id === category.id ? 'bold' : 'text-tertiary'
            }`}
            style={{
              borderBottom:
                selectedProductCategory?.id === category.id
                  ? '4px solid var(--color-primary)'
                  : '1px solid #7E7E7E',
            }}
          >
            {category.name}
          </div>
        ))}
      </div>
    );
  };

  const renderProducts = () => {
    if (loadingProducts) {
      return SkeletonReservationArea();
    }

    if (error) {
      return (
        <div className={styles.panels}>
          <div className={styles.content}>
            <div className='card'>
              <EmptyActivity title={error} />
            </div>
          </div>
        </div>
      );
    }

    if (selectedProductCategory.seatMap) {
      return (
        <>
          <SeatMap
            seatMapConfig={selectedProductCategory.seatMap}
            products={products}
            selectedProducts={seatProducts}
            eventDetail={eventDetail}
            showId={showId}
            eventId={selectedEvent.eventId}
            onSelect={(p) => handleIncrementSeat(p)}
            onDeselect={(id) => handleDecrementSeat(id)}
          />
          <div className='mt-3'>
            <CurrencySelectorWithPicker />
          </div>
        </>
      );
    }

    return (
      <>
        <div className='product-list mt-3'>
          {orderType.map((type, i) => {
            const productItems = groupProducts[type];
            if (productItems) {
              return (
                <div className='product-types' key={i}>
                  {type !== 'ALL' && (
                    <div className='fw-600 text-primary font-tiny pl-1'>
                      {productItems.ageRangeLabel}
                    </div>
                  )}
                  <div className='mt-2'>
                    {productItems.items.map((p) => (
                      <div className='mt-2' key={p.id}>
                        {p.behavior === 'NO_GUARANTEED' ? (
                          <EventProductCard
                            product={p}
                            handleIncrement={() => handleIncrement(p.id)}
                            handleDecrement={() => handleDecrement(p.id)}
                            hasMoreInfo={false}
                          />
                        ) : p.reservationType === 'AREA' ? (
                          <ProductCard
                            product={p}
                            handleIncrement={() => handleIncrement(p.id)}
                            handleDecrement={() => handleDecrement(p.id)}
                            hasMoreInfo={true}
                            onInfoClick={() => {
                              onInfoClick(p);
                            }}
                            loadingImage={loadingImage}
                            setLoadingImage={setLoadingImage}
                          />
                        ) : (
                          <ProductCard
                            product={p}
                            handleIncrement={() => handleIncrement(p.id)}
                            handleDecrement={() => handleDecrement(p.id)}
                            hasMoreInfo={true}
                            onInfoClick={() => {
                              onInfoClick(p);
                            }}
                            loadingImage={loadingImage}
                            setLoadingImage={setLoadingImage}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
        {ruleError && (
          <div className='mt-3' ref={ruleErrorRef}>
            <ErrorCard
              title={ruleError.map((msg, i) => (
                <div key={i}>{msg}</div>
              ))}
            />
          </div>
        )}
        <div className='mt-3'>
          <CurrencySelectorWithPicker />
        </div>
      </>
    );
  };

  const hasItems = products?.some((p) => p.quantity > 0) || seatProducts?.length > 0;
  const reservationType = hasItems ? products[0].reservationType : null;

  useEffect(() => {
    if (show) {
      const todayDate = getDateFromOffset(show.utcOffset);
      const formattedToday = format(todayDate, 'yyyy-MM-dd');

      setTodayDate(todayDate);
      setSelectedDate(formattedToday);
      getEvents(formattedToday);
    }
  }, [show]);

  useEffect(() => {
    getShow();
  }, []);

  return (
    <>
      {loading && show === null ? (
        <div className='loaderDiv'>
          <CircleSpinner
            className='spinner-app mx-auto'
            style={{ margin: 'auto' }}
            size={40}
            color='var(--color-font-primary)'
          />
        </div>
      ) : (
        <>
          <Header
            onClick={() =>
              app.isEmbedded
                ? sendMessageToParent(window, {
                    type: 'GO_BACK',
                  })
                : history.goBack()
            }
          />
          <ContentApp>
            <EventHeader
              eventImg={show.bannerImageUrl}
              eventName={show.name}
              eventDescription={show.city.name}
              size='small'
            />

            {!loadingEvents && emptyEvents ? (
              <div className='mt-3'>
                <EmptyActivity title={t('noResults')} />
              </div>
            ) : (
              <>
                <div className='mt-3'>
                  <DatePickerSlider
                    date={selectedDate}
                    dates={dates}
                    firstDate={todayDate}
                    handleSelect={(e) => handleSelectDate(e)}
                    calendarDates={datesCalendar}
                    onCalendarClick={handleCalendarClick}
                  />
                </div>
                {renderTimes()}
                {loadingProducts || selectedProductCategory === null ? (
                  <div className='loaderDiv'>
                    <CircleSpinner
                      className='spinner-app mx-auto'
                      style={{ margin: 'auto' }}
                      size={40}
                      color='var(--color-font-primary)'
                    />
                  </div>
                ) : (
                  <>
                    {renderProductCategories()}
                    <div className='mt-3' style={{ paddingBottom: '115px' }}>
                      <>
                        {renderProducts()}

                        {hasItems && (
                          <div
                            className='position-fixed w-100'
                            style={{
                              bottom: 0,
                              left: 0,
                              zIndex: 2,
                            }}
                          >
                            <div className='container'>
                              <div className='row justify-content-center'>
                                <div className='col-12 col-md-7 col-lg-6 p-0'>
                                  <ButtonReservation
                                    leftContent={
                                      <>
                                        <div className='font-normal'>
                                          {t('total')} <Money value={sumTotal()} />
                                        </div>
                                        <div className='font-smaller'>
                                          {reservationType.toUpperCase() === 'AREA'
                                            ? t('upToGuests', { pax: totalPax() })
                                            : t('person', { count: totalPax() })}
                                        </div>
                                        {totalConsume() > 0 && (
                                          <div className='font-smaller'>
                                            {t('consumptionCredit')}{' '}
                                            <Money value={totalConsume()} />
                                          </div>
                                        )}
                                      </>
                                    }
                                    rightContent={
                                      <div>
                                        {t('continue')}
                                        <span>
                                          <ChevronIcon size={20} fillColor={'#000'} />
                                        </span>
                                      </div>
                                    }
                                    size='large'
                                    className={`${styles.btn}`}
                                    onClick={handleContinue}
                                    fixed={true}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    </div>
                  </>
                )}
              </>
            )}
          </ContentApp>
        </>
      )}
      <ProductModal
        open={isModalOpen}
        onClose={toggleModal}
        animationClass='slide-left-right'
        fullScreen={true}
        hasBackground={true}
        backIcon={true}
      >
        <ReservationTicketInfo
          productId={selectedProduct?.id}
          showAddButton={
            selectedProduct?.stock === null ||
            (!selectedProduct?.hasSoldOut && selectedProduct?.stock > 0)
          }
          handleIncrement={handleIncrement}
          prices={selectedProduct?.prices}
          toggleModal={toggleModal}
          isModalOpen={isModalOpen}
        />
      </ProductModal>
    </>
  );
};

export default CalendarTicketSelector;
